import * as React from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./ui/command";
import { Command as CommandPrimitive } from "cmdk";
import { cn } from "../lib/utils";

/**
 * Typeable, filterable single-value combobox.
 *
 * Drop-in replacement for the Radix <Select> used in the booking forms:
 *   - Users can type to filter options.
 *   - `priorityItems` render first, followed by a gold separator and `items`.
 *   - `value` is a plain string (same as Select onValueChange contract).
 *
 * Props:
 *   value            currently selected value string ("" for none)
 *   onValueChange    (value:string) => void
 *   placeholder      trigger text when no value is picked
 *   disabled         disable the trigger
 *   priorityItems    [{ value, label, testId? }]
 *   items            [{ value, label, testId? }]
 *   triggerTestId    data-testid on the trigger button
 *   triggerClassName override for the trigger styling
 *   emptyMessage     shown when the typed query matches nothing
 *   ariaLabel        accessibility label for the input
 */
export default function SearchableSelect({
  value,
  onValueChange,
  placeholder = "Select…",
  disabled = false,
  priorityItems = [],
  items = [],
  triggerTestId,
  triggerClassName,
  emptyMessage = "No matches",
  ariaLabel = "Search location",
}) {
  const [open, setOpen] = React.useState(false);

  const allItems = React.useMemo(
    () => [...priorityItems, ...items],
    [priorityItems, items]
  );
  const selected = allItems.find((it) => it.value === value);
  const displayLabel = selected?.label || "";

  const handlePick = (val) => {
    onValueChange?.(val);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={triggerTestId}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            "input-dark w-full flex items-center justify-between gap-2 text-left",
            !displayLabel && "text-white/40",
            disabled && "opacity-50 cursor-not-allowed",
            triggerClassName
          )}
        >
          <span className="truncate">{displayLabel || placeholder}</span>
          <ChevronDown className="w-4 h-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[var(--radix-popover-trigger-width)] p-0 bg-[#1F1F1F] border-[#333] text-white"
        onOpenAutoFocus={(e) => {
          // Let cmdk focus its own input.
        }}
      >
        <Command
          className="bg-[#1F1F1F] text-white"
          filter={(itemValue, search) => {
            // itemValue is the option value string; search matches on the
            // human-readable label via the `keywords` prop below, but we
            // also fall back to matching the raw value.
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            return itemValue.toLowerCase().includes(q) ? 1 : 0;
          }}
        >
          <div className="flex items-center border-b border-[#333] px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 text-[#C5A059]" />
            <CommandPrimitive.Input
              autoFocus
              placeholder="Type to search…"
              aria-label={ariaLabel}
              className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-white/40 text-white"
              data-testid={triggerTestId ? `${triggerTestId}-search-input` : undefined}
            />
          </div>
          <CommandList className="max-h-72">
            <CommandEmpty className="py-4 text-center text-sm text-white/50">
              {emptyMessage}
            </CommandEmpty>
            {priorityItems.length > 0 && (
              <CommandGroup className="p-1">
                {priorityItems.map((it) => (
                  <ComboItem
                    key={`p-${it.value}`}
                    item={it}
                    selected={it.value === value}
                    onPick={handlePick}
                  />
                ))}
              </CommandGroup>
            )}
            {priorityItems.length > 0 && items.length > 0 && (
              <CommandSeparator className="my-1 bg-[#C5A059]/30" />
            )}
            {items.length > 0 && (
              <CommandGroup className="p-1">
                {items.map((it) => (
                  <ComboItem
                    key={it.value}
                    item={it}
                    selected={it.value === value}
                    onPick={handlePick}
                  />
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ComboItem({ item, selected, onPick }) {
  return (
    <CommandItem
      value={item.label /* cmdk filters on this string */}
      onSelect={() => onPick(item.value)}
      data-testid={item.testId}
      className={cn(
        "text-white/85 aria-selected:bg-[#C5A059]/15 aria-selected:text-[#C5A059] data-[selected=true]:bg-[#C5A059]/15 data-[selected=true]:text-[#C5A059] hover:bg-[#2A2A2A] cursor-pointer",
        selected && "text-[#C5A059]"
      )}
    >
      <span className="flex-1 truncate">{item.label}</span>
      {selected && <Check className="w-4 h-4 text-[#C5A059]" />}
    </CommandItem>
  );
}
